
import { action, observable } from 'mobx';
import {
	Model,
	IModelAttributes,
	attribute,
	entity,
	ReferencePath,
} from 'Models/Model';
import * as Models from 'Models/Entities';
import * as Validators from 'Validators';
import { CRUD } from '../CRUDOptions';
import * as AttrUtils from 'Util/AttributeUtils';
import { IAcl } from 'Models/Security/IAcl';
import {
	makeFetchOneToManyFunc,
	makeEnumFetchFunction,
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import VisitorsEsingworkflow from 'Models/Security/Acl/VisitorsEsingworkflow';
import UserEsingworkflow from 'Models/Security/Acl/UserEsingworkflow';
import * as Enums from '../Enums';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';
import { esignWorkflowStatusOptions } from 'Models/Enums/MappedEnumOptions';


export interface IEsignworkflowEntityAttributes extends IModelAttributes {
	status: Enums.esignworkflowstatus;
	stage: Enums.esignworkflowstage;
	signatorycount: number;
	rejectioncount: number;
	notificationsemail: string;
	requestcontent: string;
	legacyapplicationid: number;
	base64signedcontract: string;

	esignceremonysigneess: Array<
		| Models.EsignceremonysigneeEntity
		| Models.IEsignceremonysigneeEntityAttributes
	>;
	esigntemplatecontentId: string;
	esigntemplatecontent: Models.EsigntemplatecontentEntity | Models.IEsigntemplatecontentEntityAttributes;
	esigntemplateId: string;
	esigntemplate: Models.EsigntemplateEntity | Models.IEsigntemplateEntityAttributes;

}

@entity('EsignworkflowEntity', 'ESignWorkflow')
export default class EsignworkflowEntity extends Model
	implements IEsignworkflowEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new VisitorsEsingworkflow(),
		new UserEsingworkflow(),

	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<EsignworkflowEntity, Enums.esignworkflowstatus>()
	@CRUD({
		name: 'Status',
		displayType: 'enum-combobox',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: (attr: string) => {
			return AttrUtils.standardiseEnum(attr, esignWorkflowStatusOptions);
		},
		enumResolveFunction: makeEnumFetchFunction(esignWorkflowStatusOptions),
		displayFunction: (attr: Enums.esignworkflowstatus) => esignWorkflowStatusOptions[attr],
	})
	public status: Enums.esignworkflowstatus;

	@observable
	@attribute<EsignworkflowEntity, Enums.esignworkflowstage>()
	@CRUD({
		name: 'Stage',
		displayType: 'enum-combobox',
		order: 20,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: (attr: string) => {
			return AttrUtils.standardiseEnum(attr, Enums.esignworkflowstageOptions);
		},
		enumResolveFunction: makeEnumFetchFunction(Enums.esignworkflowstageOptions),
		displayFunction: (attr: Enums.esignworkflowstage) => Enums.esignworkflowstageOptions[attr],
	})
	public stage: Enums.esignworkflowstage;
	

	
	@Validators.Integer()
	@observable
	@attribute<EsignworkflowEntity, number>()
	@CRUD({
		name: 'SignatoryCount',
		displayType: 'textfield',
		order: 30,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseInteger,
	})
	public signatorycount: number;
	

	
	@Validators.Integer()
	@observable
	@attribute<EsignworkflowEntity, number>()
	@CRUD({
		name: 'RejectionCount',
		displayType: 'textfield',
		order: 40,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseInteger,
	})
	public rejectioncount: number;

	@observable
	@attribute<EsignworkflowEntity, string>()
	@CRUD({
		name: 'NotificationsEmail',
		displayType: 'textfield',
		order: 50,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public notificationsemail: string;

	@observable
	@attribute<EsignworkflowEntity, string>()
	@CRUD({
		name: 'RequestContent',
		displayType: 'textarea',
		order: 60,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public requestcontent: string;
	

	
	@Validators.Integer()
	@observable
	@attribute<EsignworkflowEntity, number>()
	@CRUD({
		name: 'LegacyApplicationID',
		displayType: 'textfield',
		order: 70,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseInteger,
	})
	public legacyapplicationid: number;

	@observable
	@attribute<EsignworkflowEntity, string>()
	@CRUD({
		name: 'Base64SignedContract',
		displayType: 'textarea',
		order: 80,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public base64signedcontract: string;

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		
		name: 'ESignCeremonySigneess',
		displayType: 'reference-multicombobox',
		order: 90,
		referenceTypeFunc: () => Models.EsignceremonysigneeEntity,
		disableDefaultOptionRemoval: true,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'esignceremonysigneess',
			oppositeEntity: () => Models.EsignceremonysigneeEntity,
		}),
		
	})
	public esignceremonysigneess: Models.EsignceremonysigneeEntity[] = [];

	@Validators.Required()
	@observable
	@attribute()
	@CRUD({
		
		name: 'ESignTemplateContent',
		displayType: 'reference-combobox',
		order: 100,
		referenceTypeFunc: () => Models.EsigntemplatecontentEntity,
		
	})
	public esigntemplatecontentId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public esigntemplatecontent: Models.EsigntemplatecontentEntity;

	@Validators.Required()
	@observable
	@attribute()
	@CRUD({
		
		name: 'ESignTemplate',
		displayType: 'reference-combobox',
		order: 110,
		referenceTypeFunc: () => Models.EsigntemplateEntity,
		
	})
	public esigntemplateId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public esigntemplate: Models.EsigntemplateEntity;



	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<IEsignworkflowEntityAttributes>) {
		super(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IEsignworkflowEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.status !== undefined) {
				this.status = attributes.status;
			}
			if (attributes.stage !== undefined) {
				this.stage = attributes.stage;
			}
			if (attributes.signatorycount !== undefined) {
				this.signatorycount = attributes.signatorycount;
			}
			if (attributes.rejectioncount !== undefined) {
				this.rejectioncount = attributes.rejectioncount;
			}
			if (attributes.notificationsemail !== undefined) {
				this.notificationsemail = attributes.notificationsemail;
			}
			if (attributes.requestcontent !== undefined) {
				this.requestcontent = attributes.requestcontent;
			}
			if (attributes.legacyapplicationid !== undefined) {
				this.legacyapplicationid = attributes.legacyapplicationid;
			}
			if (attributes.base64signedcontract !== undefined) {
				this.base64signedcontract = attributes.base64signedcontract;
			}
			if (attributes.esignceremonysigneess !== undefined && Array.isArray(attributes.esignceremonysigneess)) {
				for (const model of attributes.esignceremonysigneess) {
					if (model instanceof Models.EsignceremonysigneeEntity) {
						this.esignceremonysigneess.push(model);
					} else {
						this.esignceremonysigneess.push(new Models.EsignceremonysigneeEntity(model));
					}
				}
			}
			if (attributes.esigntemplatecontentId !== undefined) {
				this.esigntemplatecontentId = attributes.esigntemplatecontentId;
			}
			if (attributes.esigntemplatecontent !== undefined) {
				if (attributes.esigntemplatecontent === null) {
					this.esigntemplatecontent = attributes.esigntemplatecontent;
				} else if (attributes.esigntemplatecontent instanceof Models.EsigntemplatecontentEntity) {
					this.esigntemplatecontent = attributes.esigntemplatecontent;
					this.esigntemplatecontentId = attributes.esigntemplatecontent.id;
				} else {
					this.esigntemplatecontent = new Models.EsigntemplatecontentEntity(attributes.esigntemplatecontent);
					this.esigntemplatecontentId = this.esigntemplatecontent.id;
				}
			}
			if (attributes.esigntemplateId !== undefined) {
				this.esigntemplateId = attributes.esigntemplateId;
			}
			if (attributes.esigntemplate !== undefined) {
				if (attributes.esigntemplate === null) {
					this.esigntemplate = attributes.esigntemplate;
				} else if (attributes.esigntemplate instanceof Models.EsigntemplateEntity) {
					this.esigntemplate = attributes.esigntemplate;
					this.esigntemplateId = attributes.esigntemplate.id;
				} else {
					this.esigntemplate = new Models.EsigntemplateEntity(attributes.esigntemplate);
					this.esigntemplateId = this.esigntemplate.id;
				}
			}

		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	public defaultExpands = `
		esignceremonysigneess {
			${Models.EsignceremonysigneeEntity.getAttributes().join('\n')}
		}
		esigntemplatecontent {
			${Models.EsigntemplatecontentEntity.getAttributes().join('\n')}
		}
		esigntemplate {
			${Models.EsigntemplateEntity.getAttributes().join('\n')}
		}
	`;

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
			esignceremonysigneess: {},
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
						],
					},
				],
			},
		);
	}

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		return this.id;
	}
}

/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(EsignworkflowEntity.prototype, 'created');
CRUD(modifiedAttr)(EsignworkflowEntity.prototype, 'modified');
